import React from "react";
import {
  IconArrowLeft,
  IconCalendar,
  IconClock,
  IconMapPin,
} from "@tabler/icons-react";
import { FaGlobe } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import { notify } from "../../../components/alerts";
import Button from "../../../components/elements/form/Button";
import { formatDate } from "../../../service/convert/date";
import { enroll_training, get_training } from "../../../service/api/training";
import { useAppDispatch, useAppSelector } from "../../../store";

const TrainingDetail = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const training = useAppSelector(({ training }) => training.training);
  const isLoading = useAppSelector(({ training }) => training.loading);
  const loadingTrainingId = useAppSelector(
    ({ training }) => training.loadingTrainingId,
  );
  const user = useAppSelector(({ auth }) => auth.user);

  React.useEffect(() => {
    if (id) get_training(dispatch, id);
  }, [dispatch, id]);

  const handleApply = () => {
    if (!id) return;
    if (!user?.email) {
      notify({
        type: "info",
        message: "Registration required",
        description: "Please register to apply for this training.",
      });
      return;
    }
    enroll_training(id, user.email, dispatch);
  };

  const isApplying = loadingTrainingId === id;
  const displayTraining = training?.id === id ? training : null;

  const img =
    "https://images.pexels.com/photos/39287540/pexels-photo-39287540.jpeg";

  return (
    <div className="min-h-screen bg-gray-50/20 px-4 py-14 md:px-8 md:py-28">
      <div className="mx-auto max-w-7xl">
        <Link
          to="/training"
          className="mb-5 inline-flex items-center gap-2 rounded-lg text-sm font-medium text-primary-700 transition-colors hover:text-primary-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
        >
          <IconArrowLeft size={18} aria-hidden="true" />
          All trainings
        </Link>

        {isLoading && !displayTraining ? (
          <div
            className="animate-pulse overflow-hidden rounded-2xl border border-stone-200 bg-white"
            aria-label="Loading training details"
          >
            <div className="h-56 bg-stone-200 md:h-96" />
            <div className="space-y-4 p-6 md:p-10">
              <div className="h-8 w-2/3 rounded bg-stone-200" />
              <div className="h-4 w-full rounded bg-stone-100" />
              <div className="h-4 w-5/6 rounded bg-stone-100" />
            </div>
          </div>
        ) : displayTraining ? (
          <>
            <div className="overflow-hidden rounded-2xl border border-gray-100/50 bg-white shadow-sm">
              {img ? (
                <img
                  src={img}
                  alt={displayTraining.name}
                  className="h-56 w-full object-cover md:h-[26rem]"
                />
              ) : (
                <div className="flex h-56 items-center justify-center bg-gradient-to-br from-primary-50 to-emerald-100 px-6 text-center text-2xl font-semibold text-primary-800 md:h-[26rem] md:text-4xl">
                  {displayTraining.name}
                </div>
              )}

              <div className="grid gap-8 p-5 md:p-10 lg:grid-cols-[minmax(0,1fr)_19rem]">
                <section>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary-700">
                    Corporate Setup Training
                  </p>
                  <h1 className="text-3xl font-bold leading-tight text-stone-900 md:text-4xl">
                    {displayTraining.name}
                  </h1>

                  <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-sm text-stone-600">
                    {displayTraining.date && (
                      <span className="inline-flex items-center gap-2">
                        <IconCalendar
                          size={18}
                          className="text-primary-700"
                          aria-hidden="true"
                        />
                        {formatDate(displayTraining.date)}
                      </span>
                    )}
                    {displayTraining.duration && (
                      <span className="inline-flex items-center gap-2">
                        <IconClock
                          size={18}
                          className="text-primary-700"
                          aria-hidden="true"
                        />
                        {displayTraining.duration}
                      </span>
                    )}
                    {displayTraining.address && (
                      <span className="inline-flex items-center gap-2">
                        <IconMapPin
                          size={18}
                          className="text-primary-700"
                          aria-hidden="true"
                        />
                        {displayTraining.address}
                      </span>
                    )}
                  </div>

                  <div className="mt-8 border-t border-gray-100/50 pt-7">
                    <h2 className="text-xl font-semibold text-stone-900">
                      About this training
                    </h2>
                    <p className="mt-3 whitespace-pre-line text-base leading-7 text-stone-600">
                      {displayTraining.body ||
                        "Training details will be available soon."}
                    </p>
                  </div>
                </section>

                <aside className="h-fit rounded-xl border border-gray-100/50 bg-stone-50 p-5 md:p-6">
                  <p className="text-sm font-medium text-stone-500">
                    Training fee
                  </p>
                  <p className="mt-1 text-3xl font-bold text-stone-900">
                    {displayTraining.amount || "Free"}
                  </p>

                  <Button
                    title={isApplying ? "Applying..." : "Apply for training"}
                    loading={isApplying}
                    disabled={isApplying}
                    onHandler={handleApply}
                    className="mt-5 w-full rounded-lg bg-primary-700 py-3 text-white hover:bg-primary-800"
                  />

                  {displayTraining.link && (
                    <a
                      href={displayTraining.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-primary-700 px-4 py-3 text-center text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                    >
                      <FaGlobe aria-hidden="true" />
                      Payment and more details
                    </a>
                  )}
                </aside>
              </div>
            </div>
          </>
        ) : (
          <section className="rounded-2xl border border-stone-200 bg-white px-6 py-16 text-center shadow-sm">
            <h1 className="text-2xl font-bold text-stone-900">
              Training unavailable
            </h1>
            <p className="mt-2 text-stone-600">
              This training could not be found. It may have been removed or the
              link may be incorrect.
            </p>
            <Link
              to="/training"
              className="mt-6 inline-flex rounded-lg bg-primary-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-800"
            >
              Browse trainings
            </Link>
          </section>
        )}
      </div>
    </div>
  );
};

export default TrainingDetail;
